import React, { useState } from 'react';
import { forumHelpers, ForumTopic } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Save, Loader2 } from 'lucide-react';
import { ForumCategory } from '../lib/supabase';

interface TopicEditorProps {
  topic: ForumTopic | null;
  categories?: ForumCategory[];
  onClose: () => void;
  onTopicUpdated: (updatedTopic: ForumTopic) => void;
}

const TopicEditor: React.FC<TopicEditorProps> = ({ 
  topic, 
  categories = [], 
  onClose, 
  onTopicUpdated 
}) => {
  const [formData, setFormData] = useState({
    title: topic?.title || '',
    content: topic?.content || '',
    category_id: topic?.category_id || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!topic) {
      setError('Tópico não encontrado');
      return;
    }

    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await forumHelpers.updateTopic(topic.id, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        category_id: formData.category_id
      });

      if (error) {
        setError('Erro ao atualizar tópico');
        console.error('Erro ao atualizar tópico:', error);
      } else if (data) {
        onTopicUpdated(data);
        onClose();
      }
    } catch (err) {
      setError('Erro inesperado ao atualizar tópico');
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#121316] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#1f2329]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">
              Editar Tópico
            </h2>
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-white">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Digite o título do tópico..."
                className="bg-[#0f1114] border-[#1f2329] text-white placeholder-gray-500"
              />
            </div>

            <div>
              <Label htmlFor="category" className="text-white">Categoria</Label>
              <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                <SelectTrigger className="bg-[#0f1114] border-[#1f2329] text-white">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1d23] border-[#1f2329]">
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id} className="text-white hover:bg-[#0FFF55]/10">
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="content" className="text-white">Conteúdo</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Escreva o conteúdo do tópico..."
                rows={8}
                className="bg-[#0f1114] border-[#1f2329] text-white placeholder-gray-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopicEditor; 